import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";

// httpOnly cookies are managed by the browser automatically.
// No token storage needed on the client side.

let refreshPromise: Promise<boolean> | null = null;

function originBase(): string {
	return typeof window !== "undefined" ? window.location.origin : "";
}

async function refreshTokens(): Promise<boolean> {
	try {
		const res = await globalThis.fetch(`${originBase()}/auth/refresh`, {
			method: "POST",
			credentials: "include",
		});
		return res.ok;
	} catch {
		return false;
	}
}

function startOrJoinRefresh(): Promise<boolean> {
	if (!refreshPromise) {
		refreshPromise = refreshTokens().finally(() => {
			refreshPromise = null;
		});
	}
	return refreshPromise;
}

const authMiddleware: Middleware = {
	async onResponse({ response, request }) {
		if (response.status !== 401) return;

		const url = new URL(request.url);
		if (url.pathname.startsWith("/auth/")) return;

		const success = await startOrJoinRefresh();
		if (!success) {
			window.location.href = "/login";
			return;
		}

		// Build a fresh Request - the original was already consumed by the first fetch.
		const retry = new Request(request.url, {
			method: request.method,
			headers: request.headers,
			body:
				request.method === "GET" || request.method === "HEAD"
					? null
					: await request.clone().arrayBuffer(),
			credentials: "include",
			redirect: request.redirect,
		});
		return globalThis.fetch(retry);
	},
};

export const fetchClient = createClient<paths>({
	baseUrl: typeof window !== "undefined" ? window.location.origin : "",
	credentials: "include",
	fetch: (...args) => globalThis.fetch(...args),
});
fetchClient.use(authMiddleware);
